import React from 'react';
import { 
  Box, 
  ToggleButton, 
  ToggleButtonGroup, 
  Slider, 
  Typography,
  Paper
} from '@mui/material';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import TabletIcon from '@mui/icons-material/Tablet';
import LaptopIcon from '@mui/icons-material/Laptop';
import DesktopWindowsIcon from '@mui/icons-material/DesktopWindows';

export interface DeviceSize {
  width: number;
  height: number;
  name: string;
}

interface DeviceControlsProps {
  isPro: boolean;
  onDeviceChange: (size: DeviceSize) => void;
  onCustomSizeChange: (width: number, height: number) => void;
  currentDevice: string;
}

export const DEVICE_PRESETS = {
  mobile: { width: 375, height: 667, name: 'Mobile' },
  tablet: { width: 768, height: 1024, name: 'Tablet' },
  laptop: { width: 1366, height: 768, name: 'Laptop' },
  desktop: { width: 1920, height: 1080, name: 'Desktop' }
};

export const DeviceControls: React.FC<DeviceControlsProps> = ({
  isPro,
  onDeviceChange,
  onCustomSizeChange,
  currentDevice
}) => {
  const [customWidth, setCustomWidth] = React.useState(DEVICE_PRESETS.mobile.width);
  const [customHeight, setCustomHeight] = React.useState(DEVICE_PRESETS.mobile.height);

  React.useEffect(() => {
    if (currentDevice && currentDevice !== 'custom') {
      const preset = DEVICE_PRESETS[currentDevice as keyof typeof DEVICE_PRESETS];
      if (preset) {
        setCustomWidth(preset.width);
        setCustomHeight(preset.height);
      }
    }
  }, [currentDevice]);

  const handleDeviceChange = (
    _event: React.MouseEvent<HTMLElement>,
    newDevice: string | null
  ) => {
    if (newDevice && DEVICE_PRESETS[newDevice as keyof typeof DEVICE_PRESETS]) {
      const preset = DEVICE_PRESETS[newDevice as keyof typeof DEVICE_PRESETS];
      setCustomWidth(preset.width);
      setCustomHeight(preset.height);
      onDeviceChange(preset);
    }
  };

  const handleCustomSizeChange = (dimension: 'width' | 'height', value: number) => {
    if (dimension === 'width') {
      setCustomWidth(value);
    } else {
      setCustomHeight(value);
    }
    onCustomSizeChange(
      dimension === 'width' ? value : customWidth,
      dimension === 'height' ? value : customHeight
    );
  };

  return (
    <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Device Type {!isPro && '(Pro Feature)'}
        </Typography>
        <ToggleButtonGroup
          value={currentDevice}
          exclusive
          onChange={handleDeviceChange}
          disabled={!isPro}
        >
          <ToggleButton value="mobile">
            <PhoneIphoneIcon />
          </ToggleButton>
          <ToggleButton value="tablet">
            <TabletIcon />
          </ToggleButton>
          <ToggleButton value="laptop">
            <LaptopIcon />
          </ToggleButton>
          <ToggleButton value="desktop">
            <DesktopWindowsIcon />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {isPro && (
        <Box sx={{ width: '100%' }}>
          <Typography variant="subtitle2" gutterBottom>
            Custom Size
          </Typography>
          <Box sx={{ px: 2 }}>
            <Typography>Width: {customWidth}px</Typography>
            <Slider
              value={customWidth}
              onChange={(_e, val) => handleCustomSizeChange('width', val as number)}
              min={320}
              max={1920}
              step={1}
            />
            <Typography>Height: {customHeight}px</Typography>
            <Slider
              value={customHeight}
              onChange={(_e, val) => handleCustomSizeChange('height', val as number)}
              min={500}
              max={1080}
              step={1}
            />
          </Box>
        </Box>
      )}
    </Paper>
  );
}; 